/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useCallback, useContext} from 'react'
import {MultiSiteContext} from '../contexts'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'

/**
 * Retrieves Einstein information for a given site ID from the application configuration.
 *
 * This function searches through the list of sites in the application configuration
 * to find a site that matches the provided site ID. If a matching site is found, it
 * returns the Einstein ID and Einstein Site ID associated with that site. If no
 * matching site is found or if the application configuration is not properly set up,
 * it returns null values for both Einstein ID and Einstein Site ID.
 *
 * @param {string} siteId - The unique identifier of the site for which Einstein information is requested.
 * @returns {Object} An object containing the Einstein ID and Einstein Site ID for the specified site.
 *                   If the site is not found or the configuration is invalid, both values will be null.
 * @property {string|null} einsteinId - The Einstein ID associated with the site, or null if not found.
 * @property {string|null} einsteinSiteId - The Einstein Site ID associated with the site, or null if not found.
 */
function getEinsteinInfoBySiteId(siteId) {
    const {app: appConfig} = getConfig()

    if (!appConfig || appConfig?.sites.length === 0) {
        return {
            einsteinId: null,
            einsteinSiteId: null
        }
    }

    const sites = appConfig?.sites
    const site = sites?.find((site) => site.id === siteId)

    if (!site) {
        return {
            einsteinId: null,
            einsteinSiteId: null
        }
    }

    return {
        einsteinId: site.einsteinId || null,
        einsteinSiteId: site.einsteinSiteId || null
    }
}

/**
 * Determines if a given site ID corresponds to an outlet site based on the app configuration.
 * @param {string} siteId - The unique identifier of the site to check.
 * @returns {boolean} True if the site is configured as an outlet site, otherwise false.
 */
function isOutletSite(siteId) {
    if (!siteId) {
        return false
    }

    const {app: appConfig} = getConfig()

    const siteConfig = appConfig?.sites?.find((site) => site.id === siteId)

    return siteConfig?.isOutlet === true
}

/**
 * Custom React hook to get the function that returns usefule multi-site values, the site, the locale, the einsteinId, the einsteinSiteId and
 * the funtion used to build URLs following the App configuration.
 * @returns {{site, locale, buildUrl: (function(*, *, *): *), einsteinId: string, einsteinSiteId: string}}
 */
const useMultiSite = () => {
    const context = useContext(MultiSiteContext)
    const location = useLocation()

    if (context === undefined) {
        throw new Error('useMultiSite must be used within MultiSiteProvider')
    }
    const {buildUrl: originalFn, site, locale, setLocale} = context
    const {einsteinId, einsteinSiteId} = getEinsteinInfoBySiteId(site?.id)

    const buildUrl = useCallback(
        (path, siteRef, localeRef) => {
            return originalFn(
                path,
                siteRef ? siteRef : site?.alias || site?.id,
                localeRef ? localeRef : locale?.alias || locale?.id
            )
        },
        [originalFn, site, locale]
    )
    const buildURLWithUTM = useCallback(
        (path, siteRef, localeRef) => {
            const searchParams = new URLSearchParams(location.search)
            const newSearchParams = new URLSearchParams()
            // Iterate over searchParams and set UTM parameters directly
            searchParams.forEach((value, key) => {
                if (key.startsWith('utm_') && value) {
                    newSearchParams.set(key, value)
                }
            })

            return {
                pathname: buildUrl(path, siteRef, localeRef),
                search: newSearchParams.toString()
            }
        },
        [buildUrl, location.search]
    )
    return {
        site,
        locale,
        setLocale,
        buildUrl,
        einsteinId,
        einsteinSiteId,
        isOutletSite: isOutletSite(site?.id),
        buildURLWithUTM
    }
}

export default useMultiSite
